import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { I2gDialogService } from 'i2g-ng-ui';
import { compact, isEmpty } from 'lodash';
import { concat, EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  flatMap,
  map,
  mergeMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { AccountDataStore } from '../account-data/account-data.store';
import { User } from '../account-data/user.model';
import { DialogService } from '../dialog/dialog.service';
import { NavigationService } from '../navigation/navigation.service';
import { DIALOG_TYPE as manualPaymentDialog } from '../payments/components/manual-payment-dialog/manual-payment-dialog.component';
import { DIALOG_TYPE as onboardingStripeDialog } from '../payments/components/onboarding-stripe-dialog/onboarding-stripe-dialog.component';
import * as fromSettings from '../settings-data/settings-data.actions';
import {
  FetchStripeOnboardingUrl,
  IFetchPayPalOnboardingUrl,
} from '../settings-data/settings-data.actions';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { Settings } from '../settings-data/settings.model';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import { MessageToastComponent } from '../shared/components/message-toast/message-toast.component';
import { PayPalOnboardingDialogComponent } from '../shared/components/payments/paypal-onboarding-dialog/paypal-onboarding-dialog.component';
import * as fromToast from '../toaster/toaster.actions';
import { IToasterConfig, IToasterData } from '../toaster/toaster.data';
import { PaymentsDataStateService } from './payments-data-state.service';
import {
  ActionsEnum,
  fetchPayPalTransactionHistoryError,
  fetchPayPalTransactionHistorySuccess,
  GetTransactionHistoryUrl,
  getTransactionHistoryUrlError,
  getTransactionHistoryUrlSuccess,
  IFetchPayPalTransactionHistory,
  IFetchPayPalTransactionHistoryError,
  IFetchPayPalTransactionHistorySuccess,
  IFetchPayPalTransactionHistorySuccessPayload,
  ITriggerPayPalOnboardingCanvas,
  SendReceipt,
  SendReceiptError,
  sendReceiptError,
  SendReceiptPreview,
  sendReceiptPreviewError,
  SendReceiptPreviewError,
  sendReceiptPreviewSuccess,
  SendReceiptPreviewSuccess,
  SendReceiptSuccess,
  sendReceiptSuccess,
  SendRemindersPreview,
  TriggerStripeOnboardingCanvas,
} from './payments-data.actions';
import { ITransactionHistoryUrlResponse } from './payments-data.data';
import { PaymentsDataService } from './payments-data.service';

@Injectable()
export class PaymentsDataEffects {
  constructor(
    private _actions: Actions,
    private _accountDataStore: AccountDataStore,
    private _paymentsDataService: PaymentsDataService,
    private _dialogService: DialogService,
    private _i2gDialogService: I2gDialogService,
    private _navigationService: NavigationService,
    private _translateService: TranslateService,
    private _globalBannerService: GlobalBannerService,
    private _settingsDataStore: SettingsDataStore,
    private _paymentsDataStateService: PaymentsDataStateService
  ) {}

  @Effect()
  sendReceipt: Observable<
    | SendReceiptSuccess
    | SendReceiptError
    | fromSettings.UpdateInitialPaymentReceiptsSettings
  > = this._actions.pipe(
    ofType(ActionsEnum.SEND_RECEIPT),
    flatMap(({ payload }: SendReceipt) => {
      const {
        dispatchId,
        documentId,
        recipient,
        transactionId,
        rendererOptions,
        updatePaymentReceiptsSettings,
      } = payload;

      return this._paymentsDataService
        .sendReceipt(documentId, transactionId, rendererOptions)
        .pipe(
          flatMap(() =>
            concat(
              compact([
                sendReceiptSuccess(dispatchId, recipient),
                updatePaymentReceiptsSettings
                  ? fromSettings.updateInitialPaymentReceiptsSettings(true)
                  : null,
              ])
            )
          ),
          catchError((error) => of(sendReceiptError(dispatchId, error)))
        );
    })
  );

  @Effect()
  sendReceiptSuccess: Observable<fromToast.ShowToast> = this._actions.pipe(
    ofType(ActionsEnum.SEND_RECEIPT_SUCCESS),
    filter(({ payload }: SendReceiptSuccess) => !isEmpty(payload.recipient)),
    map(({ payload }: SendReceiptSuccess) => {
      const { recipient } = payload;
      const label = this._translateService.instant('PAYMENTS.RECEIPT_SENT', {
        recipient,
      });
      const toastData: IToasterData = { label, containsHtml: true };
      const toastConfig: IToasterConfig = {
        type: 'receipt-sent',
        component: MessageToastComponent,
        data: toastData,
      };

      return fromToast.showToast(toastConfig);
    })
  );

  @Effect({ dispatch: false })
  sendReceiptError = this._actions.pipe(
    ofType(ActionsEnum.SEND_RECEIPT_ERROR),
    tap(({ payload }: SendReceiptError) =>
      this._globalBannerService.showSystemErrorBanner(payload.error)
    )
  );

  @Effect()
  sendReceiptPreview: Observable<
    SendReceiptPreviewSuccess | SendReceiptPreviewError
  > = this._actions.pipe(
    ofType(ActionsEnum.SEND_RECEIPT_PREVIEW),
    flatMap(({ payload }: SendReceiptPreview) => {
      const { dispatchId, document, transaction, rendererOptions } = payload;

      return this._paymentsDataService
        .sendReceiptPreview(document, transaction, rendererOptions)
        .pipe(
          map(() => sendReceiptPreviewSuccess(dispatchId)),
          catchError((error) => of(sendReceiptPreviewError(dispatchId, error)))
        );
    })
  );

  @Effect()
  sendReceiptPreviewSuccess: Observable<fromToast.ShowToast> = this._actions.pipe(
    ofType(ActionsEnum.SEND_RECEIPT_PREVIEW_SUCCESS),
    concatMap(() => {
      return this._accountDataStore.getUser().pipe(
        map((user: User) => {
          const label = this._translateService.instant(
            'PAYMENTS.RECEIPT_SENT',
            {
              recipient: user.email,
            }
          );
          const toastData: IToasterData = { label, containsHtml: true };
          const toastConfig: IToasterConfig = {
            type: 'receipt-sent',
            component: MessageToastComponent,
            data: toastData,
          };

          return fromToast.showToast(toastConfig);
        })
      );
    })
  );

  @Effect({ dispatch: false })
  sendRemindersPreview: Observable<any> = this._actions.pipe(
    ofType(ActionsEnum.SEND_REMINDERS_PREVIEW),
    flatMap(({ payload }: SendRemindersPreview) =>
      this._paymentsDataService.sendRemindersPreview(payload.reminderDays)
    )
  );

  @Effect({ dispatch: false })
  sendInvoiceUnopenedPreview: Observable<any> = this._actions.pipe(
    ofType(ActionsEnum.SEND_INVOICE_UNOPENED_PREVIEW),
    flatMap(() => this._paymentsDataService.sendInvoiceUnopenedPreview())
  );

  @Effect()
  getTransactionHistory: Observable<any> = this._actions.pipe(
    ofType(ActionsEnum.GET_TRANSACTION_HISTORY_URL),
    flatMap(({ payload }: GetTransactionHistoryUrl) => {
      const { dispatchId, documentId, transactionId } = payload;

      return this._paymentsDataService
        .fetchTransactionDetailsUrl(documentId, transactionId)
        .pipe(
          map((body: ITransactionHistoryUrlResponse) => {
            if (body.url) {
              this._navigationService.goToExternalUrl(body.url, true);
            }
            return getTransactionHistoryUrlSuccess(dispatchId);
          }),
          catchError((error) =>
            of(getTransactionHistoryUrlError(dispatchId, error))
          )
        );
    })
  );

  @Effect()
  triggerStripeOnboardingCanvas: Observable<FetchStripeOnboardingUrl> = this._actions.pipe(
    ofType(ActionsEnum.TRIGGER_STRIPE_ONBOARDING_CANVAS),
    map(({ payload }: TriggerStripeOnboardingCanvas) => {
      const { enableCardPayments, href } = payload;
      const returnUrl = href || this._navigationService.getLocationHref();

      this._dialogService.closeDialog({ type: manualPaymentDialog }); // close KIC dialog

      this._dialogService.openDialog({ type: onboardingStripeDialog });

      return fromSettings.fetchStripeOnboardingUrl(
        enableCardPayments,
        returnUrl
      );
    })
  );

  @Effect()
  triggerPayPalOnboardingCanvas: Observable<
    IFetchPayPalOnboardingUrl | never
  > = this._actions.pipe(
    ofType(ActionsEnum.TRIGGER_PAYPAL_ONBOARDING_CANVAS),
    mergeMap(({ payload }: ITriggerPayPalOnboardingCanvas) => {
      // open dialog and show loader while we fetch webview url
      this._i2gDialogService.openDialog(PayPalOnboardingDialogComponent, {
        isDismissible: true,
      });

      return of(fromSettings.fetchPayPalOnboardingUrl(payload.returnUrl));
    })
  );

  @Effect()
  fetchPayPalTransactionHistory: Observable<
    IFetchPayPalTransactionHistorySuccess | IFetchPayPalTransactionHistoryError
  > = this._actions.pipe(
    ofType(ActionsEnum.FETCH_PAYPAL_TRANSACTION_HISTORY),
    mergeMap(({ payload }: IFetchPayPalTransactionHistory) => {
      const { timestamp } = payload;

      return this._paymentsDataService
        .fetchPayPalTransactionHistory(timestamp)
        .pipe(
          map(
            ({
              transactions,
              nextPageTimestamp,
            }: IFetchPayPalTransactionHistorySuccessPayload) =>
              fetchPayPalTransactionHistorySuccess(
                transactions,
                nextPageTimestamp
              )
          ),
          catchError((error) => {
            this._globalBannerService.showSystemErrorBanner(error);

            return of(fetchPayPalTransactionHistoryError(error));
          })
        );
    })
  );
}
